// Crisis Detection System
// Monitors user messages for concerning keywords and triggers appropriate responses

import crisisKeywords from '../data/crisis_keywords.json';

/**
 * Detect crisis level from user message
 * @param {string} message - User's message text
 * @returns {Object} - { level: 'none'|'tier1'|'tier2', matchedKeywords: [], reason: '' }
 */
export const detectCrisisLevel = (message) => {
  if (!message || typeof message !== 'string') {
    return { level: 'none', matchedKeywords: [], reason: '' };
  }

  const lowerMessage = message.toLowerCase();
  
  // Check Tier 2 first (most severe)
  const tier2Matches = crisisKeywords.tier2.keywords.filter(keyword => 
    lowerMessage.includes(keyword.toLowerCase())
  );
  
  if (tier2Matches.length > 0) {
    return {
      level: 'tier2',
      matchedKeywords: tier2Matches,
      reason: 'Immediate safety concern detected'
    };
  }
  
  // Check Tier 1 (pause & stabilize)
  const tier1Matches = crisisKeywords.tier1.keywords.filter(keyword => 
    lowerMessage.includes(keyword.toLowerCase())
  );
  
  if (tier1Matches.length > 0) {
    return {
      level: 'tier1',
      matchedKeywords: tier1Matches,
      reason: 'Emotional distress detected'
    };
  }
  
  return { level: 'none', matchedKeywords: [], reason: '' };
};

/**
 * Get appropriate response for crisis level
 * @param {string} level - Crisis level ('tier1' or 'tier2')
 * @returns {Object} - Response configuration
 */
export const getCrisisResponse = (level) => {
  if (level === 'tier2') {
    return {
      title: "I'm Concerned About Your Safety",
      message: "It sounds like you're going through something really difficult right now. Your safety is the most important thing.",
      actions: crisisKeywords.tier2.actions,
      resources: crisisKeywords.tier2.resources,
      showGroundingOnly: true,
      suspendPractices: true
    };
  }
  
  if (level === 'tier1') {
    return {
      title: "Let's Pause for a Moment",
      message: "I notice you might be feeling overwhelmed. Let's take a moment to ground ourselves before continuing.",
      actions: crisisKeywords.tier1.actions,
      resources: null,
      showGroundingOnly: false,
      suspendPractices: false,
      limitIntensePractices: true
    };
  }
  
  return null;
};

/**
 * Log crisis incident to Firestore for safety records
 * @param {string} userId - User ID
 * @param {Object} incident - Incident details
 */
export const logCrisisIncident = async (userId, incident) => {
  try {
    const { doc, setDoc, serverTimestamp } = await import('firebase/firestore');
    const { db } = await import('../firebaseConfig');
    
    const incidentRef = doc(db, 'users', userId, 'crisisLogs', `incident_${Date.now()}`);
    
    await setDoc(incidentRef, {
      ...incident,
      timestamp: serverTimestamp(),
      userAgent: navigator.userAgent,
      resolved: false
    });
    
    console.log('🚨 Crisis incident logged');
  } catch (error) {
    console.error('Error logging crisis incident:', error);
    // Don't throw - logging failure shouldn't break the flow
  }
};

/**
 * Check if user should be limited to light practices
 * @param {string} userId - User ID
 * @returns {boolean} - True if user has recent Tier 1 incidents (within 7 days)
 */
export const shouldLimitIntensePractices = async (userId) => {
  try {
    const { collection, query, where, getDocs, Timestamp } = await import('firebase/firestore');
    const { db } = await import('../firebaseConfig');
    
    const sevenDaysAgo = Timestamp.fromDate(new Date(Date.now() - 7 * 24 * 60 * 60 * 1000));
    
    const logsRef = collection(db, 'users', userId, 'crisisLogs');
    const q = query(
      logsRef,
      where('level', '==', 'tier1'),
      where('timestamp', '>', sevenDaysAgo)
    );
    
    const snapshot = await getDocs(q);
    return !snapshot.empty;
  } catch (error) {
    console.error('Error checking crisis history:', error);
    return false; // Default to not limiting if check fails
  }
};

/**
 * Get grounding exercise (5-4-3-2-1 technique)
 * @returns {Object} - Grounding exercise steps
 */
export const getGroundingExercise = () => {
  return {
    title: "5-4-3-2-1 Grounding Technique",
    intro: "This simple exercise can help you feel more present and calm. Take your time with each step.",
    steps: [
      {
        number: 5,
        instruction: "Name 5 things you can SEE around you",
        example: "A blue chair, a plant, a coffee mug, a window, a book"
      },
      {
        number: 4,
        instruction: "Name 4 things you can TOUCH or FEEL",
        example: "Your feet on the floor, your back against the chair, the texture of your clothes, the air on your skin"
      },
      {
        number: 3,
        instruction: "Name 3 things you can HEAR",
        example: "Birds chirping, distant traffic, your own breathing"
      },
      {
        number: 2,
        instruction: "Name 2 things you can SMELL",
        example: "Coffee, fresh air, laundry detergent, nothing at all"
      },
      {
        number: 1,
        instruction: "Name 1 thing you can TASTE",
        example: "Mint from toothpaste, coffee, or just the taste in your mouth"
      }
    ],
    closing: "Take a deep breath. Notice how you feel now compared to a few moments ago."
  };
};

/**
 * Format crisis resources for display
 * @param {Object} resources - Resources from crisis_keywords.json
 * @param {string} country - User's country (optional)
 * @returns {Array} - Formatted resources
 */
export const formatCrisisResources = (resources, country = 'US') => {
  if (!resources) return [];
  
  // Always show global resources
  const formatted = [
    ...resources.global.map(r => ({ ...r, region: 'Global' }))
  ];
  
  // Add country-specific if available
  if (country === 'US' && resources.us) {
    formatted.unshift(...resources.us.map(r => ({ ...r, region: 'US' })));
  }
  
  if (country === 'India' && resources.india) {
    formatted.unshift(...resources.india.map(r => ({ ...r, region: 'India' })));
  }
  
  return formatted;
};

/**
 * Send crisis alert email (would need backend implementation)
 * This is a placeholder for future backend integration
 * @param {string} userId - User ID
 * @param {Object} incident - Incident details
 */
export const sendCrisisAlert = async (userId, incident) => {
  // TODO: Implement backend endpoint to send email
  // For now, just log
  console.warn('🚨 CRISIS ALERT - Would send email:', { userId, incident });
  
  // In production, this would call:
  // await fetch('/api/crisis-alert', {
  //   method: 'POST',
  //   body: JSON.stringify({ userId, incident })
  // });
};
